// Strømprikker: små lyse prikker, der løber i strømmens retning. Én ParticleContainer, én tekstur.

import { Particle, ParticleContainer } from 'pixi.js';
import type { Texture } from 'pixi.js';
import { bez } from './geo';
import type { View } from './view';

export class FlowDots {
  readonly pc: ParticleContainer;
  private parts: Particle[] = [];
  private shown = 0;

  constructor(private texture: Texture) {
    this.pc = new ParticleContainer({
      texture,
      dynamicProperties: { position: true, vertex: true, rotation: false, uvs: false, color: true },
    });
  }

  update(v: View): void {
    const budget = v.quality.dots;
    const t = v.now / 1000;
    let n = 0;
    for (const l of v.state.lines) {
      if (l.status !== 'ok' || Math.abs(l.flow) < 1) continue;
      const geo = v.lines.get(l.id);
      if (!geo || (v.drawIn.get(l.id) ?? 1) < 1) continue;
      const load = Math.min(1.2, l.loadPm / 1000);
      const count = Math.max(1, Math.min(24, Math.round((geo.len / (34 * v.k)) * (0.5 + load))));
      const speed = (26 + 70 * load) * v.k;
      const dir = l.flow > 0 ? 1 : -1;
      const size = (l.tier === 1 ? 0.74 : l.tier === 2 ? 0.92 : 1.08) * v.k * (l.halfDown ? 0.8 : 1);
      for (let i = 0; i < count && n < budget; i++) {
        let u = ((t * speed) / geo.len + i / count) % 1;
        if (dir < 0) u = 1 - u;
        const p = bez(geo.a, geo.c, geo.b, u);
        const part = this.get(n);
        part.x = p.x;
        part.y = p.y;
        // Prikkerne fader ind og ud i enderne, så de ikke hopper.
        const edge = Math.min(u, 1 - u) * 8;
        part.alpha = Math.min(1, edge);
        part.scaleX = size;
        part.scaleY = size;
        n++;
      }
    }
    // Første strøm på nye ledninger: en større prik med tre små efter sig, uden for budgettet
    // (højst fire prikker pr. ny ledning, og kun i knap et halvt sekund).
    for (const [id, s] of v.surges) {
      const l = v.state.lines.find((x) => x.id === id);
      if (!l || l.status !== 'ok' || Math.abs(l.flow) < 1) continue;
      const geo = v.lines.get(id);
      if (!geo) continue;
      const size = (l.tier === 1 ? 0.62 : l.tier === 2 ? 0.8 : 0.95) * v.k * 1.7;
      // Ind på de første 10 %, ud på de sidste 20 %.
      const env = Math.min(1, s.u * 10, (1 - s.u) * 5);
      for (let j = 0; j < 4; j++) {
        const back = j * (14 * v.k) / Math.max(1, geo.len);
        const u0 = s.u - back;
        if (u0 < 0) break;
        const u = l.flow > 0 ? u0 : 1 - u0;
        const p = bez(geo.a, geo.c, geo.b, u);
        const part = this.get(n);
        part.x = p.x;
        part.y = p.y;
        part.alpha = env * (1 - j * 0.24);
        part.scaleX = size * (1 - j * 0.2);
        part.scaleY = part.scaleX;
        n++;
      }
    }
    if (n !== this.shown) {
      this.pc.particleChildren.length = 0;
      for (let i = 0; i < n; i++) this.pc.particleChildren.push(this.parts[i] as Particle);
      this.pc.update();
      this.shown = n;
    }
  }

  private get(i: number): Particle {
    let p = this.parts[i];
    if (!p) {
      p = new Particle({ texture: this.texture, anchorX: 0.5, anchorY: 0.5 });
      this.parts.push(p);
    }
    return p;
  }
}
